import { InitPage } from "../index";

declare var $: any;
declare var App: any;

export interface SampleCatalogProgressData {
    message: string;
    progress: number;
    total: number;
    current: number;
    inProgress: boolean;
    success: boolean;
    cancelled: boolean;
}

// Drives the "Download Sample Data" modal on the integration edit screen.
// Subscribes to the dedicated SampleCatalog::ProgressChannel (same ActionCable
// pattern as the other integration progress channels) and updates the modal's
// progress bar / total count live. The download runs synchronously in the
// request for API integrations, or in a worker for Selenium ones; either way
// progress and completion arrive over this channel.
class SampleCatalogProgressUpdate {
    subscription: any = null;
    started = false;
    finished = false;

    constructor() {
        this.bindEvents();
    }

    bindEvents() {
        $(document).on("change", "#catalogDownloadView input[type=radio]", () => this.onModeChange());
        $(document).on("submit", "#catalog-download-form", (e: any) => this.onSubmit(e));
        $(document).on("click", "#sample-catalog-cancel", () => this.cancelOrClose());
        $(document).on("click", "#sample-catalog-again", () => this.showOptions());

        $(document).on("integrations:subview-shown", (_e: any, id: string) => {
            if (id === "catalogDownloadView") this.onModalShown();
        });
        $(document).on("integrations:subview-closed", () => this.cleanup());
    }

    onModalShown() {
        this.started = false;
        this.finished = false;
        this.onModeChange();
        if (this.tenantCode()) this.subscribe();
    }

    isTruncate(): boolean {
        return $('#catalogDownloadView input[name="truncate_catalog"]:checked').val() === "true";
    }

    isSample(): boolean {
        return $('#catalogDownloadView input[name="download_kind"]:checked').val() === "sample";
    }

    onSubmit(e: any) {
        const $start = $("#catalog-download-start");
        if (this.isSample()) {
            e.preventDefault();
            this.start();
            return;
        }
        if ($start.prop("disabled")) {
            e.preventDefault();
            return;
        }
        $start.prop("disabled", true).text($start.data("starting-label"));
        $("#sample-catalog-cancel").prop("disabled", true);
    }

    onModeChange() {
        const $modal = $("#catalogDownloadView");
        const truncate = this.isTruncate();
        const kind = this.isSample() ? "sample" : "full";
        $modal.find(".choice").each((_: number, el: HTMLElement) => {
            $(el).toggleClass("on", $(el).find("input").is(":checked"));
        });
        $modal.find("[data-kind]").each((_: number, el: HTMLElement) => {
            $(el).toggle($(el).data("kind") === kind);
        });
        $modal.find(".truncate-warning").toggle(truncate);
        const $start = $("#catalog-download-start");
        $start.toggleClass("danger", truncate).text($start.data(truncate ? "truncate-label" : "start-label"));
    }

    showOptions() {
        this.started = false;
        this.finished = false;
        $("#sample-catalog-options").css("display", "flex");
        $("#sample-catalog-progress-area, #sample-catalog-result").hide();
        $("#catalogDownloadView .sample-result").hide();
        $("#sample-catalog-message").text("");
        this.setLocked(false);
        $("#sample-catalog-running-note, #sample-catalog-catalog-link, #sample-catalog-again").hide();
        $("#catalog-download-start").prop("disabled", false).show();
        this.setCancelLabel("back-label");
        this.onModeChange();
    }

    showResult(kind: string, message?: string) {
        const $result = $(`#catalogDownloadView .sample-result[data-result="${kind}"]`);
        if (kind === "failed") {
            const error = String(message || "").replace(/^Sample download failed:?\s*/i, "").trim();
            $result.find(".sample-result-text").text(error
                ? String($result.data("template")).replace("%{error}", error)
                : String($result.data("fallback")));
            $("#sample-catalog-progress-area").hide();
        } else if (message) {
            $result.find(".sample-result-text").text(message);
        }
        $("#catalogDownloadView .sample-result").hide();
        $result.css("display", "flex");
        $("#sample-catalog-result").show();
        this.setLocked(false);
        $("#sample-catalog-running-note").hide();
        $("#sample-catalog-catalog-link").toggle(kind === "done");
        $("#sample-catalog-again").toggle(kind !== "done");
    }

    tenantCode(): string {
        return $("#catalogDownloadView").data("tenant-code");
    }

    subscribe() {
        if (typeof App === "undefined" || !App.cable) { return; }
        this.unsubscribe();

        const tc = this.tenantCode();
        this.subscription = App.cable.subscriptions.create(
            {
                channel: "SampleCatalog::ProgressChannel",
                tenant_code: tc,
                roomId: `sample_catalog_${tc}`
            },
            {
                connected() { console.log("Connected to Sample Catalog Progress Channel"); },
                disconnected() { console.log("Disconnected from Sample Catalog Progress Channel"); },
                received: (data: any) => this.onProgress(data)
            }
        );
    }

    unsubscribe() {
        if (this.subscription) {
            try { this.subscription.unsubscribe(); } catch (e) {}
            this.subscription = null;
        }
    }

    cleanup() {
        this.unsubscribe();
        this.started = false;
        this.finished = false;
    }

    setLocked(locked: boolean) {
        $("#editIntegration").toggleClass("int-subview-locked", locked);
        $("#catalogDownloadView button.int-subview-back").prop("disabled", locked);
    }

    setCancelLabel(label: string) {
        const $btn = $("#sample-catalog-cancel");
        $btn.prop("disabled", false).text($btn.data(label));
    }

    setProgress(p: number) {
        p = Math.max(0, Math.min(100, Math.round(p) || 0));
        $("#sample-catalog-progress-bar").css("width", `${p}%`);
        $("#catalogDownloadView .sample-progress").attr("aria-valuenow", p);
        $("#sample-catalog-progress-label").text(`${p}%`);
    }

    onProgress(data: SampleCatalogProgressData) {
        if (!data) { return; }
        // Once completed, ignore any late/out-of-order frames so the bar can't
        // regress below 100%.
        if (!this.started || this.finished) { return; }

        if (typeof data.total !== "undefined" && data.total !== null) {
            $("#sample-catalog-total").text(Number(data.total).toLocaleString());
        }
        if (typeof data.progress !== "undefined") { this.setProgress(data.progress); }
        if (data.message) { $("#sample-catalog-message").text(data.message); }
        if (data.inProgress === false) { this.markDone(data); }
    }

    markDone(data: Partial<SampleCatalogProgressData>) {
        // Completion signal (sync request return, or the websocket terminal
        // frame for worker-based runs): force the bar to 100% on success so a
        // late/out-of-order websocket frame can't leave it stuck just below 100.
        if (data && data.success !== false && !data.cancelled) {
            this.setProgress(100);
        }

        if (this.finished) { return; }
        this.finished = true;

        this.setCancelLabel("back-label");

        if (data && data.cancelled) {
            this.showResult("cancelled");
        } else if (data && data.success === false) {
            this.showResult("failed", (data as any).error || (data as any).message || $("#sample-catalog-message").text());
        } else {
            this.setProgress(100);
            $("#sample-catalog-message").text("Finished");
            this.showResult("done");
        }
    }

    csrf(): string {
        return $('meta[name="csrf-token"]').attr("content");
    }

    start() {
        const $modal = $("#catalogDownloadView");
        const truncate = this.isTruncate();

        $("#sample-catalog-options, #sample-catalog-result").hide();
        $("#sample-catalog-progress-area").css("display", "flex");
        $("#catalog-download-start").prop("disabled", true).hide();
        $("#sample-catalog-again, #sample-catalog-catalog-link").hide();
        $("#sample-catalog-running-note").show();
        this.setLocked(true);
        this.setCancelLabel("cancel-label");
        $("#sample-catalog-message").text(truncate ? "Emptying the catalog…" : "Connecting…");
        this.started = true;
        this.finished = false;
        this.setProgress(0);
        $("#catalogDownloadView .sample-progress").toggleClass("danger", truncate);

        // Make sure we're subscribed before kicking off the synchronous run.
        if (!this.subscription) { this.subscribe(); }

        $.ajax({
            url: $modal.data("start-url"),
            method: "POST",
            dataType: "json",
            headers: { "X-CSRF-Token": this.csrf() },
            data: { truncate_catalog: truncate },
            success: (res: any) => {
                // Selenium integrations run in a worker and answer { status: "started" }:
                // completion arrives on the websocket terminal frame (inProgress=false),
                // so don't mark done here. Synchronous integrations return the final
                // result, which is the authoritative completion signal.
                if (res && res.status === "started") { return; }
                this.markDone(res || { success: true });
            },
            error: (xhr: any) => {
                const msg = (xhr.responseJSON && (xhr.responseJSON.error || xhr.responseJSON.message)) || "";
                this.markDone({ success: false, message: msg });
            }
        });
    }

    cancelOrClose() {
        const $modal = $("#catalogDownloadView");
        if (!this.started || this.finished) { $(document).trigger("integrations:subview-back"); return; }

        const $btn = $("#sample-catalog-cancel");
        $btn.prop("disabled", true).text($btn.data("cancelling-label"));
        $.ajax({
            url: $modal.data("cancel-url"),
            method: "POST",
            dataType: "json",
            headers: { "X-CSRF-Token": this.csrf() },
            success: () => {
                this.unsubscribe();
                this.markDone({ cancelled: true });
            },
            error: () => this.setCancelLabel("cancel-label")
        });
    }
}

export function LoadPage() {
    InitPage("integrations", () => {
        const sampleCatalog = new SampleCatalogProgressUpdate();
    });
}
